/**
 * @module ui/dropdown-menu
 */
import ee from "lib/eventemitter";
import { qs, getById } from "utils/utils";
import logger from "utils/logger";
import PositionHelper from "utils/PositionHelper";
import merge from "lodash/merge";

const debugTagBase = "[DROPDOWN-MENU]";

const DropdownPrototype = {

  /**
   * Initialize the dropdown menu
   * @param {HTMLElement} triggerEl - The dropdown menu's trigger button
   * @param {Object} [options={}] - Options for the instance
   * @param {Boolean} options.autoAdjustHorizontal - Whether to automatically adjust the position along the horizontal axis
   * @param {Boolean} options.autoAdjustVertical - Whether to automatically adjust the position along the vertical axis
   */
  init(triggerEl, options = {}) {
    if (!triggerEl) {
      return;
    }
    this.trigger = triggerEl;

    this.menu = getById(triggerEl.getAttribute("aria-controls"));
    if (!this.menu) {
      return;
    }
    // Just in case the "hidden" attribute isn't already present on the Dropdown Menu
    this.menu.setAttribute("hidden", "hidden");

    this.closeButton = qs(".js-dropdown-menu-close", this.menu);
    this.options = options;

    this.onBodyClickFn = this.onBodyClick.bind(this);
    this.onBodyFocusFn = this.onBodyFocus.bind(this);
    this.onBodyKeydownFn = this.onBodyKeydown.bind(this);

    this.openFn = this.open.bind(this);
    this.closeFn = this.close.bind(this);
    this.toggleFn = this.toggle.bind(this);
    this.onTransitionEndFn = this.onTransitionEnd.bind(this);

    this.onCloseButtonClickFn = this.onCloseButtonClick.bind(this);

    this.position = new PositionHelper(this.trigger, this.menu, "c-dropdown-menu", options);

    this.debugTag = `${debugTagBase}[${this.trigger.id}]`;
    logger.info(`${this.debugTag} init`, options);
  },

  /**
   * Activate the dropdown menu by attaching the relevant event listeners
   *
   * Some touch-enabled devices will also try to emulate mouse events,
   * so both touch and mouse events will fire on touch:
   * @see https://www.html5rocks.com/en/mobile/touchandmouse/#toc-together
   * Normally, we'd be able to prevent the emulated mouse events from firing
   * after the touch events by calling "event.preventDefault()" inside a
   * "touchstart" event handler, but Windows tablets (e.g. Surface) have touch
   * events disabled by default. This means that "touchstart" will never fire,
   * but the emulated mouse events will. This makes it impossible to determine
   * whether the user tapped or clicked on the element.
   * @see https://github.com/MicrosoftEdge/Status/issues/336
   * @see https://stackoverflow.com/questions/33300962/touch-api-e-g-touchstart-not-working-in-ms-edge
   * @see https://caniuse.com/#search=touch
   * To make things even worse, when a user taps on an element, not only do the
   * "mousedown", "mouseup" and "click" events fire (which is to be expected),
   * but the "mouseenter" and "mouseleave" events fire too! This conflicts with
   * the dropdown menu's mouse hover functionality, so we've decided to avoid
   * enabling this functionality on devices that support multiple touchpoints.
   * @see https://hacks.mozilla.org/2013/04/detecting-touch-its-the-why-not-the-how/
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Navigator/maxTouchPoints
   */
  activate() {
    logger.info(`${this.debugTag} activate`);

    document.addEventListener("click", this.onBodyClickFn, true);
    document.addEventListener("focus", this.onBodyFocusFn, true);
    document.addEventListener("keydown", this.onBodyKeydownFn, true);

    this.trigger.addEventListener("mousedown", this.toggleFn);
    this.trigger.addEventListener("keydown", this.toggleFn);

    this.menu.addEventListener("transitionend", this.onTransitionEndFn, false);

    if (this.closeButton) {
      this.closeButton.addEventListener("click", this.onCloseButtonClickFn);
    }
    if (this.isHoverEnabled()) {
      this.activateHover();
    } else {
      this.deactivateHover();
    }
  },

  /**
   * Deactivate the dropdown menu by removing the relevant event listeners
   */
  deactivate() {
    logger.info(`${this.debugTag} deactivate`);

    document.removeEventListener("click", this.onBodyClickFn);
    document.removeEventListener("focus", this.onBodyFocusFn);
    document.removeEventListener("keydown", this.onBodyKeydownFn);

    this.trigger.removeEventListener("mousedown", this.toggleFn);
    this.trigger.removeEventListener("keydown", this.toggleFn);

    this.menu.removeEventListener("transitionend", this.onTransitionEndFn);

    if (this.closeButton) {
      this.closeButton.removeEventListener("click", this.onCloseButtonClickFn);
    }
    this.deactivateHover();
  },

  /**
   * Attach event listeners that are required for mouse hover functionality
   */
  activateHover() {
    logger.info(`${this.debugTag} activateHover`);

    this.trigger.addEventListener("mouseenter", this.openFn);
    this.trigger.addEventListener("mouseleave", this.closeFn);

    this.menu.addEventListener("mouseenter", this.openFn);
    this.menu.addEventListener("mouseleave", this.closeFn);
  },

  /**
   * Remove event listeners that are required for mouse hover functionality
   */
  deactivateHover() {
    logger.info(`${this.debugTag} deactivateHover`);

    this.trigger.removeEventListener("mouseenter", this.openFn);
    this.trigger.removeEventListener("mouseleave", this.closeFn);

    this.menu.removeEventListener("mouseenter", this.openFn);
    this.menu.removeEventListener("mouseleave", this.closeFn);
  },

  /**
   * Whether or not the dropdown menu will open when its trigger menu is hovered
   * @returns {Boolean}
   */
  isHoverEnabled() {
    return this.menu.classList.contains("js-dropdown-menu-hover-enabled") && !navigator.maxTouchPoints;
  },

  /**
   * Determine whether the dropdown is in an open state
   * @returns {Boolean} Whether or not the dropdown menu is currently open
   */
  isOpen() {
    const style = window.getComputedStyle(this.menu);
    const opacity = Number(style.getPropertyValue("opacity"));
    return opacity > 0 && this.trigger.getAttribute("aria-expanded") === "true";
  },

  /**
   * Determine whether the dropdown is in a closed state
   * @returns {Boolean} Whether or not the dropdown menu is currently closed
   */
  isClosed() {
    const style = window.getComputedStyle(this.menu);
    const opacity = Number(style.getPropertyValue("opacity"));
    return opacity < 1 && this.trigger.getAttribute("aria-expanded") !== "true";
  },

  /**
   * Adjust the dropdown menu's position, if necessary, then open it
   * @param {Event} [event]
   */
  open(event) {
    const evt = event ? `(${event.type})` : "";

    if (this.isOpen()) {
      return;
    }
    logger.info(`${this.debugTag} open ${evt}`);

    this.menu.removeAttribute("hidden");
    this.position.setPosition();
    this.menu.classList.add("c-dropdown-menu--open");
    this.trigger.setAttribute("aria-expanded", true);

    ee.trigger("dropdownMenu:open", [this]);
    if (this.options.onOpenFn) {
      this.options.onOpenFn(this);
    }
    ee.trigger("dropdownMenu:opened", [this]);
  },

  /**
   * Close the dropdown menu
   * @param {Event} [event]
   */
  close(event) {
    const evt = event ? `(${event.type})` : "";

    if (this.isClosed()) {
      return;
    }
    logger.info(`${this.debugTag} close ${evt}`);

    this.menu.classList.remove("c-dropdown-menu--open");
    this.trigger.setAttribute("aria-expanded", false);

    ee.trigger("dropdownMenu:close", [this]);
    if (this.options.onCloseFn) {
      this.options.onCloseFn(this);
    }
    ee.trigger("dropdownMenu:closed", [this]);
  },

  /**
   * Add a "hidden" attribute after the Dropdown menu's hide transition has completed
   * @param {TransitionEvent} event
   */
  onTransitionEnd(event) {
    if (event.propertyName === "opacity" && !this.menu.classList.contains("c-dropdown-menu--open")) {
      logger.info(`${this.debugTag} onTransitionEnd`);
      this.menu.setAttribute("hidden", "hidden");
    }
  },

  /**
   * Toggle the dropdown menu open and closed
   * @param {MouseEvent|KeyboardEvent} event - A mousedown or keydown event
   */
  toggle(event) {
    if (event.type !== "keydown" || (event.type === "keydown" && (event.key === "Enter" || event.key === " "))) {
      logger.info(`${this.debugTag} toggle (${event.type})`);

      if (this.isOpen() && this.options.onToggleFn(this)) {
        this.close();
      } else if (this.isClosed() && this.options.onToggleFn(this)) {
        this.open();
      }
    }
  },

  /**
   * When the dropdown menu's close button is clicked (assuming it has one),
   * close the menu and focus the trigger button
   */
  onCloseButtonClick() {
    this.trigger.focus();
    this.close();
  },

  /**
   * Close the dropdown menu when the user clicks on something other than
   * the menu or its trigger button
   * @param {MouseEvent} event - A click event
   */
  onBodyClick(event) {
    const target = event.target;
    let shouldClose = false;

    if (this.options.boundaryContainer) {
      shouldClose = this.options.boundaryContainer !== target && !this.options.boundaryContainer.contains(target);
    } else {
      shouldClose = this.trigger !== target && !this.trigger.contains(target) && this.menu !== target && !this.menu.contains(target);
    }
    if (shouldClose) {
      this.close();
    }
  },

  /**
   * Close the dropdown menu when the user focuses an element that's outside
   * of the menu and its trigger button
   */
  onBodyFocus() {
    const activeEl = document.activeElement;
    let shouldClose = false;

    if (this.options.boundaryContainer) {
      shouldClose = this.options.boundaryContainer !== activeEl && !this.options.boundaryContainer.contains(activeEl);
    } else {
      shouldClose = this.trigger !== activeEl && !this.trigger.contains(activeEl) && this.menu !== activeEl && !this.menu.contains(activeEl);
    }
    if (shouldClose) {
      this.close();
    }
  },

  /**
   * Close the dropdown menu when the user presses the escape key
   * @param {KeyboardEvent} event - A keydown event
   */
  onBodyKeydown(event) {
    if (event.keyCode === 27 && this.isOpen()) {
      this.onCloseButtonClick();
    }
  }

};

/**
 * Initialize the behaviour of a given dropdown menu
 * @see https://ux.theglobeandmail.com/pattern-library/components/dropdown-menu/
 * @param {String} triggerSelector - query selector for the dropdown menu's trigger button
 * @param {Object} [options = {}] - for menu instance
 * @returns {Object} A dropdown menu instance
 */
export default function init(triggerSelector, options = {}) {
  const trigger = qs(triggerSelector);
  if (!trigger) {
    logger.info(`${debugTagBase} not found: ${triggerSelector}`);
    return;
  }

  const props = merge({
    autoAdjustHorizontal: true,
    autoAdjustVertical: false,
    onOpenFn: () => {}, // callback triggered when toggled open
    onCloseFn: () => {}, // callback triggered when toggled closed
    onToggleFn: () => true, // return true if menu can be toggled or not.
    boundaryContainer: null // menu closes if user navigates to an element outside of this container
  }, options);

  const menu = Object.create(DropdownPrototype);
  menu.init(trigger, props);
  menu.activate();

  return menu;
}
